import React, { useMemo, useState } from 'react';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import { Search } from 'lucide-react';

const DOT_ICONS = [
  'dot-red', 'dot-blue', 'dot-green', 'dot-orange', 'dot-purple', 'dot-amber', 'dot-brown',
];

const SERVICE_ICONS = [
  'service-acoustic-guitar', 'service-aeroplane', 'service-air-conditioner', 'service-airplane',
  'service-alarm-clock', 'service-alcoholic-drinks', 'service-atm', 'service-bathtub',
  'service-bed', 'service-bed-1', 'service-beer', 'service-bellboy', 'service-bellboy-1',
  'service-bowling', 'service-briefcase', 'service-bunk', 'service-cctv', 'service-champagne',
  'service-chef', 'service-clock', 'service-clock-1', 'service-close', 'service-close-1',
  'service-cocktail', 'service-coconaut', 'service-coffee-cup', 'service-credit-card',
  'service-cutlery', 'service-cutlery-1', 'service-dumbbell', 'service-elevator',
  'service-elevator-1', 'service-exchange', 'service-fireplace', 'service-flip-flops',
  'service-french-fries', 'service-gamepad', 'service-hairdresser', 'service-hanger',
  'service-hotel', 'service-hotel-1', 'service-hotel-2', 'service-hotel-3', 'service-hotel-4',
  'service-hotel-5', 'service-hotel-6', 'service-hotel-7', 'service-hotel-8', 'service-key',
  'service-laptop', 'service-maid', 'service-map', 'service-map-1', 'service-menu',
  'service-minibar', 'service-money', 'service-money-1', 'service-mortgage', 'service-no-smoking',
  'service-notebook', 'service-notes', 'service-open', 'service-parking', 'service-passport',
  'service-passport-1', 'service-photo-camera', 'service-picture', 'service-poker',
  'service-reception', 'service-rent', 'service-ring', 'service-room-key', 'service-room-key-1',
  'service-safebox', 'service-shirt', 'service-shirt-1', 'service-shower', 'service-sofa',
  'service-stationary-bike', 'service-sun-glasses', 'service-sun-umbrella', 'service-sunglasses',
  'service-tag', 'service-taxi', 'service-taxi-1', 'service-telephone', 'service-television',
  'service-television-1', 'service-television-2', 'service-television-3', 'service-terrace',
  'service-ticket', 'service-toilet-paper', 'service-trolley', 'service-wifi',
  'service-wine-bottle', 'service-wine-menu', 'service-woman',
];

const PLACE_ICONS = [...DOT_ICONS, ...SERVICE_ICONS];

const formatLabel = (slug) => slug.replace(/^(service-|weather-|dot-)/, '').replace(/-/g, ' ');

export const placeIconUrl = (icon) => icon ? `/place-icons/${icon}.svg` : null;

export { PLACE_ICONS };

export const PlaceIconPicker = ({ value, onChange }) => {
  const [search, setSearch] = useState('');

  const filteredService = useMemo(
    () => SERVICE_ICONS.filter((icon) => formatLabel(icon).includes(search.toLowerCase())),
    [search],
  );

  const showDots = !search;

  return (
    <div className="space-y-2" data-testid="place-icon-picker">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium">Icon *</span>
        {value ? (
          <div className="flex items-center gap-1.5" data-testid="selected-place-icon">
            <img src={placeIconUrl(value)} alt={value} className="w-5 h-5" />
            <span className="text-[10px] capitalize text-muted-foreground truncate">{formatLabel(value)}</span>
          </div>
        ) : null}
      </div>

      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search icons..."
          className="h-8 text-base md:h-7 md:text-xs pl-7"
          data-testid="place-icon-search"
        />
      </div>

      <ScrollArea className="h-[148px] border rounded-md dark:bg-[#172031]" data-testid="place-icon-grid-scroll">
        <div className="grid grid-cols-7 gap-0.5 p-1">
          {showDots ? DOT_ICONS.map((icon) => (
            <button
              key={icon}
              type="button"
              title={formatLabel(icon)}
              className={`w-full aspect-square rounded-md flex items-center justify-center transition-all hover:bg-muted ${value === icon ? 'ring-2 ring-[#2563eb] bg-[#2563eb]/10' : ''}`}
              onClick={() => onChange(icon)}
              data-testid={`place-icon-${icon}`}
            >
              <img src={placeIconUrl(icon)} alt={icon} className="w-7 h-7" />
            </button>
          )) : null}
          {filteredService.map((icon) => (
            <button
              key={icon}
              type="button"
              title={formatLabel(icon)}
              className={`w-full aspect-square rounded-md flex items-center justify-center transition-all hover:bg-muted ${value === icon ? 'ring-2 ring-[#2563eb] bg-[#2563eb]/10' : ''}`}
              onClick={() => onChange(icon)}
              data-testid={`place-icon-${icon}`}
            >
              <img src={placeIconUrl(icon)} alt={icon} className="w-7 h-7" loading="lazy" />
            </button>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};
